import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, X, ArrowLeft, ArrowRight } from 'lucide-react';
import { createPortal } from 'react-dom';
import { defaultSuccessStoryImages } from '../data/successStoriesImages';

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="fixed inset-0 bg-black/80 transition-opacity backdrop-blur-sm" 
        onClick={onClose}
      />
      <motion.div 
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.3 }}
        className="relative z-50 bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-auto"
      >
        {children}
      </motion.div>
    </div>,
    document.body
  );
};

const SuccessStoriesSlider = ({ images = defaultSuccessStoryImages }) => {
  const sliderRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const scrollRight = () => {
    if (sliderRef.current) {
      const scrollAmount = sliderRef.current.offsetWidth * 0.8;
      sliderRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const scrollLeft = () => {
    if (sliderRef.current) {
      const scrollAmount = sliderRef.current.offsetWidth * 0.8;
      sliderRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="w-full py-16 bg-transparent" dir="rtl">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center mb-12">
          <Sparkles className="w-8 h-8 text-amber-500 mb-2" />
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-800 mb-2">
            סיפורי הצלחה
          </h2>
          <p className="text-lg text-gray-600 max-w-lg">
            מקרים שבהם לקוחותינו זכו לפתיחת דף חדש בחיים.
          </p>
        </div>
        
        <div className="relative">
          <div 
            ref={sliderRef}
            // By setting the mobile width to 3/4, we ensure a portion of the next image is always visible, encouraging scrolling.
            className="flex overflow-x-auto gap-6 pb-4 snap-x snap-mandatory hide-scrollbar"
          >
            {images.map((src, index) => (
              <motion.div
                key={src}
                className="w-3/4 md:w-1/2 lg:w-1/3 flex-shrink-0 snap-center"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.5 }}
                onClick={() => setSelectedImage(src)}
              >
                <div className="aspect-w-16 aspect-h-9 w-full rounded-xl overflow-hidden shadow-md cursor-pointer transform transition-transform hover:scale-105">
                  <img
                    src={src}
                    alt={`Success Story ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Navigation Buttons for Desktop */}
          <button
            onClick={scrollRight}
            className="hidden md:flex absolute top-1/2 right-0 -translate-y-1/2 p-2 bg-white rounded-full shadow-lg border border-gray-200 hover:bg-gray-100 transition-colors"
          >
            <ArrowRight className="w-6 h-6 text-gray-600" />
          </button>
          <button
            onClick={scrollLeft}
            className="hidden md:flex absolute top-1/2 left-0 -translate-y-1/2 p-2 bg-white rounded-full shadow-lg border border-gray-200 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Modal for Fullscreen Image */}
      <Modal isOpen={!!selectedImage} onClose={() => setSelectedImage(null)}>
        <div className="relative p-4">
          <button 
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 z-50 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
          <img
            src={selectedImage}
            alt="Fullscreen"
            className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
          />
        </div>
      </Modal>
    </div>
  );
};

export default SuccessStoriesSlider;
